import toast from 'react-hot-toast';
import { Button } from '@/components/ui/CustomButton';
import { useAuth } from '@/context/AuthContext';
import { useDialog } from '@gokrew/ui/context';
import {
  AsyncComboBoxField,
  FormField,
  FormikWithSchema,
} from '@gokrew/ui/form';
import { Form, type FormikHelpers } from 'formik';
import type { DialogComponentProps } from '@/types/dialog';
import type { Department, DepartmentFormValues } from '@/types';
import { departmentSchema } from '@/validation/departmentSchema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createDepartment, updateDepartment } from '@/api/department';
import { TranslationNS } from '@/constants/translation-namespaces';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import { useLinkedBranches } from '@/hooks/branches/useBranches';
import { useMemo } from 'react';
import { QueryKeys } from '@gokrew/models/constants';
interface DepartmentFormProps extends DialogComponentProps {
  department?: Department;
}

export function DepartmentForm({
  dialogId,
  onSubmit,
  onClose,
  department,
}: DepartmentFormProps) {
  const isEdit = !!department;
  const { companyId, selectedCompany } = useAuth();
  const { closeDialog } = useDialog();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.Department);
  const { data: branches, isError } = useLinkedBranches();

  if (isError) {
    toast.error(t('toast_message.branches_fetch_error'));
  }

  /**
   * A function that is called after a successful submission of the form.
   * It invalidates the "departments" query cache, shows a success toast,
   * and calls the onSubmit and onClose functions if provided.
   */
  const afterSuccessHandler = () => {
    const operation = isEdit
      ? 'toast_message.updated'
      : 'toast_message.created';
    queryClient.invalidateQueries({ queryKey: [QueryKeys.Departments] });
    toast.success(t(operation));
    if (onSubmit) onSubmit();
    if (dialogId) closeDialog(dialogId);
  };

  /**
   * A mutation hook for creating a new department.
   * It calls the createDepartment API function and handles the success and error cases.
   */
  const createMutation = useMutation({
    mutationFn: (value: DepartmentFormValues) =>
      createDepartment(companyId!, value),
    onSuccess: afterSuccessHandler,
  });

  /**
   * A mutation hook for updating department.
   * It calls the updateDepartment API function and handles the success and error cases.
   */
  const updateMutation = useMutation({
    mutationFn: (value: DepartmentFormValues) =>
      updateDepartment(companyId!, department!.id, value),
    onSuccess: afterSuccessHandler,
  });

  /**
   * Closes the dialog with the given dialogId.
   * This function is used to close the dialog after a successful submission or cancellation.
   */
  const closeDialogHandler = () => {
    if (dialogId) closeDialog(dialogId);
  };

  /**
   * Cancels the form submission and closes the dialog.
   * It calls the closeDialogHandler function to close the dialog with the given dialogId,
   * and if onClose is provided, it calls the onClose function to notify the parent component
   * that the dialog has been closed.
   */
  const cancelHandler = () => {
    closeDialogHandler();
    if (onClose) onClose();
  };

  /**
   * Handles the form submission by calling the appropriate mutation (create or update)
   * and handling the success and error cases.
   *
   * @param {DepartmentFormValues} values - The form values to be submitted
   * @param {FormikHelpers<T>} { setSubmitting } - The Formik helpers object
   */
  const saveHandler = async <T,>(
    values: DepartmentFormValues,
    { setSubmitting }: FormikHelpers<T>
  ) => {
    try {
      if (isEdit) await updateMutation.mutateAsync(values);
      else await createMutation.mutateAsync(values);
    } catch (e) {
      toast.error(
        t(isEdit ? 'toast_message.update_error' : 'toast_message.create_error')
      );
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /** Initializes the form values with the provided designation data. */
  const initialValues = useMemo(() => {
    let branchIds: string[] = [];

    if (department?.id) {
      branchIds = department?.branchMappings?.map((m) => m?.branch?.id) ?? [];
    } else if (branches?.data?.length) {
      branchIds = branches.data.map((b) => b.id);
    }

    return {
      name: department?.name ?? '',
      description: department?.description ?? '',
      branchIds,
    };
  }, [
    department?.id,
    department?.name,
    department?.description,
    department?.branchMappings,
    branches?.data,
  ]);

  /** A mapping of button labels based on the form mode (create or edit). */
  const btnLabels = {
    create: [t('buttons.create'), t('buttons.creating')],
    edit: [t('buttons.update'), t('buttons.updating')],
  };
  /** Determines the mode of the form (create or edit) based on the isEdit flag. */
  const mode = isEdit ? 'edit' : 'create';

  return (
    <FormikWithSchema
      initialValues={initialValues}
      enableReinitialize
      validationSchema={departmentSchema(t)}
      onSubmit={saveHandler}
    >
      {({ isSubmitting }) => (
        <Form>
          {/* Name Form Field */}
          <FormField
            name="name"
            label={t('form.fields.name.label')}
            type="text"
            placeholder={t('form.fields.name.placeholder')}
          ></FormField>

          {/* Description Form Field */}
          <FormField
            name="description"
            label={t('form.fields.description.label')}
            type="text"
            placeholder={t('form.fields.description.placeholder')}
          ></FormField>

          {/* Description Form Field */}
          {selectedCompany?.isMultiBranchEnabled && (
            <AsyncComboBoxField
              name="branchIds"
              label={t('form.fields.branch.label')}
              placeholder={t('form.fields.branch.placeholder')}
              labelHint={t('form.fields.branch.labelHint')}
              useHook={useLinkedBranches}
              multiple
              withSelectAll
              none={false}
              mapOptions={(branchData) =>
                branchData?.data?.map((d) => ({
                  value: d.id,
                  label: d.name,
                })) ?? []
              }
            />
          )}
          <div className="flex items-center gap-2 justify-end mt-1.5">
            <Button
              variant="outline"
              type="button"
              onClick={cancelHandler}
              className="cursor-pointer"
            >
              {t('buttons.cancel')}
            </Button>
            <FormikSubmitButton>
              {isSubmitting ? btnLabels[mode][1] : btnLabels[mode][0]}
            </FormikSubmitButton>
          </div>
        </Form>
      )}
    </FormikWithSchema>
  );
}
